import apiClient from '../index';
import { getStaff } from '../staff/staffApi';
import { NOTIFICATION_TEMPLATES, NOTIFICATION_TYPE_MAP } from './notificationConstants';

const getStaffProfile = async () => {
  try {
    const staffProfile = await getStaff();
    if (!staffProfile?.staffId) {
      throw new Error('No authenticated staff found');
    }
    return staffProfile;
  } catch (error) {
    console.warn('Staff profile not available:', error.message);
    return null;
  }
};

const mapNotificationType = (oldType) => {
  return NOTIFICATION_TYPE_MAP[oldType] || oldType || 'info';
};

const formatNotificationData = (data) => {
  return {
    ...data,
    description: data.message || data.description || "",
    type: mapNotificationType(data.type),
    priority: data.priority || "medium"
  };
};

/**
 * Get all staff notifications
 * @returns {Promise<Array>} Array of staff notifications
 */
export const getAllStaffNotifications = async () => {
  const staffProfile = await getStaffProfile();
  if (!staffProfile) {
    return [];
  }
  
  try {
    const response = await apiClient.get(`/api/snotify`);
    return Array.isArray(response.data) ? response.data : 
           Array.isArray(response.data.data) ? response.data.data : [];
  } catch (error) {
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

/**
 * Get a specific staff notification by ID
 * @param {string} notificationId - The ID of the notification to retrieve
 * @returns {Promise<Object>} The notification data
 */
export const getStaffNotificationById = async (notificationId) => {
  if (!notificationId) {
    throw new Error('Notification ID is required');
  }

  const response = await apiClient.get(`/api/snotify/${notificationId}`);
  return response.data;
};

/**
 * Create a new staff notification
 * @param {Object} notificationData - The notification data to create
 * @returns {Promise<Object>} The created notification
 */
export const createStaffNotification = async (notificationData) => {
  const staffProfile = await getStaffProfile();
  if (!staffProfile) {
    throw new Error('Staff profile required to create notification');
  }

  const formatted = formatNotificationData(notificationData);
  const response = await apiClient.post('/api/snotify/', {
    ...formatted,
    staffId: staffProfile.staffId
  });
  return response.data;
};

/**
 * Create a notification from a predefined template 
 * @param {string} templateName - The name of the template to use
 * @param {Object} customData - Custom data to override template defaults
 * @returns {Promise<Object>} The created notification
 */
export const createTemplateNotification = async (templateName, customData = {}) => {
  const staffProfile = await getStaffProfile();
  if (!staffProfile) {
    throw new Error('Staff profile required to create notification');
  }

  const template = NOTIFICATION_TEMPLATES[templateName];
  if (!template) {
    throw new Error(`Template "${templateName}" not found`);
  }

  const notificationData = {
    ...template,
    staffId: staffProfile.staffId,
    description: customData.customMessage || customData.message || template.description,
    ...(customData.link && { link: customData.link })
  };

  return createStaffNotification(notificationData);
};

/**
 * Delete a specific staff notification
 * @param {string} notificationId - The ID of the notification to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteStaffNotification = async (notificationId) => {
  const staffProfile = await getStaffProfile();
  if (!staffProfile) {
    throw new Error('Staff profile required to delete notification');
  }
  
  try {
    const response = await apiClient.delete(`/api/snotify/${notificationId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      console.warn(`Notification ${notificationId} already deleted or not found`);
      return { success: true, message: 'Notification removed' };
    }
    throw error;
  }
};

/**
 * Delete all staff notifications
 * @returns {Promise<Object>} Response data
 */
export const deleteAllStaffNotifications = async () => {
  const staffProfile = await getStaffProfile();
  if (!staffProfile) {
    throw new Error('Staff profile required to delete all notifications');
  }
  
  try {
    const response = await apiClient.delete(`/api/snotify`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { success: true, message: 'No notifications to delete' };
    }
    throw error;
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - The ID of the notification to mark as read
 * @returns {Promise<Object>} Response data
 */
export const markStaffNotificationAsRead = async (notificationId) => {
  const staffProfile = await getStaffProfile();
  if (!staffProfile) {
    throw new Error('Staff profile required to mark notification as read');
  }
  
  const response = await apiClient.patch(`/api/snotify/${notificationId}`, {
    read: true
  });
  return response.data;
};